import React, { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Form, Container, Message, Input, TextArea, Checkbox } from 'semantic-ui-react';
import { Loading } from '../../Components/UI/Loaders';
import { SubmitButton, CancelButton } from '../../Components/UI/Buttons';
import { PageTitle, SelectedItemLight  } from '../../Components/UI/Content';
import { StateContext } from '../../Application/Context/StateContext';
import useReadNotification from '../../Hooks/Notifications/useReadNotification';
import useUpdateNotification from '../../Hooks/Notifications/useUpdateNotification';
import useCreateCalendarEvent from '../../Hooks/CalendarEvents/useCreateCalendarEvent';
import useReadBusinessPartner from '../../Hooks/BusinessPartners/useReadBusinessPartner';
import useReadActivity from '../../Hooks/Activities/useReadActivity';
import useReadProject from '../../Hooks/Project/useReadProject';
import SearchBusinessPartners from '../../Components/BusinessPartners/SearchBusinessPartners';
import SearchProjects from '../../Components/Projects/SearchProjects';
import SearchActivities from '../../Components/Activities/SearchActivities';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NewCalendarEvent from '../../Components/Outlook/OutlookEvent';

const EditNotification = (props) => {

    const { t } = useTranslation();
    const history = useHistory();
    const scollToTopRef = useRef();

    /* Edit notification */
    const { notificationId } = useParams();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ date, setDate ] = useState(new Date());
    const [ title, setTitle ] = useState("");
    const [ description, setDescription ] = useState("");
    const [ businessUnit, setBusinessUnit ] = useState(1);
    const [ isRelatedToCustomer, setIsRelatedToCustomer ] = useState(false);
    const [ selectedBusinessPartner, setSelectedBusinessPartner ] = useState(false);
    const [ isRelatedToActivity, setIsRelatedToActivity ] = useState(false);
    const [ selectedActivity, setSelectedActivity ] = useState(null);
    const [ isRelatedToProject, setIsRelatedToProject ] = useState(false);
    const [ selectedProject, setSelectedProject ] = useState(null);

    /* Outlook event */
    const [ addToOutlook, setAddToOutlook ] = useState(false);
    const [ startDate, setStartDate ] = useState(() => {
        const d = new Date(date);
        d.setHours(d.getHours() + 1);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        return d;
    });
    const [ endDate, setEndDate ] = useState(() => {
        const d = new Date(date);
        d.setHours(d.getHours() + 2);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        return d;
    });
    const [ location, setLocation ] = useState("");

    const { userProfile } = useContext(StateContext);
    const { notification, readNotification } = useReadNotification();
    const { businessPartner, readBusinessPartner } = useReadBusinessPartner();
    const { activity, readActivity } = useReadActivity();
    const { project, readProject } = useReadProject();
    const { updateNotification } = useUpdateNotification();
    const { createCalendarEvent } = useCreateCalendarEvent();

    const userIsAdmin = userProfile && userProfile.IsAdmin ? true : false;
    const projectHasEnded = project && project.EndDate ? true : false; // If project has end date, project is ended and only admin can edit it!
    const projectIsNotActive = project && project.Status > 7 ? true : false; // if project status is greater than 7 (not active anymore) only admin can edit it!
    const disableEditing = (projectHasEnded || projectIsNotActive) && !userIsAdmin ? true : false;

    const [ isValidSelectedCustomer, setIsValidSelectedCustomer ] = useState(true);
    const [ isValidSelectedProject, setIsValidSelectedProject ] = useState(true);
    const [ isValidSelectedActivity, setIsValidSelectedActivity ] = useState(true);

    // Read notification
    useEffect(() => {
        if (notificationId) {
            (async() => {
                scollToTopRef.current.scrollIntoView();
                setIsLoading(true);
                await readNotification(notificationId);
            })();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationId]);

    // Read additional data
    useEffect(() => {
        (async() => {
            if (notification && notification.Project) {
                await readProject(notification.Project);
            }
            if (notification && notification.Activity) {
                await readActivity(notification.Activity);
            }
            if (notification && notification.BusinessPartner) {
                await readBusinessPartner(notification.BusinessPartner);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notification]);

    // Set notification data
    useEffect(() => {
        if (notification) {
            if (notification.Date) setDate(new Date(notification.Date));
            if (notification.Title) setTitle(notification.Title);
            if (notification.Description) setDescription(notification.Description);
            if (notification.BusinessUnit) setBusinessUnit(notification.BusinessUnit);
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notification]);
        
    // Set project data
    useEffect(() => {
        if (project && project.Id) {
            setIsRelatedToProject(true);
            setSelectedProject(project);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]);

    // Set activity data
    useEffect(() => {
        if (activity && activity.Id) {
            setIsRelatedToActivity(true);
            setSelectedActivity(activity)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity]);

    // Set businessPartner data
    useEffect(() => {
        if (businessPartner && businessPartner.Id) {
            setIsRelatedToCustomer(true);
            setSelectedBusinessPartner(businessPartner);
        }
     // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessPartner]);
    
    // Save data on submit
    const onSubmit = async () => {
        try {

            if (isRelatedToCustomer && !selectedBusinessPartner) return setIsValidSelectedCustomer(false);
            if (isRelatedToProject && !selectedProject) return setIsValidSelectedProject(false);
            if (isRelatedToActivity && !selectedActivity) return setIsValidSelectedActivity(false);
            if (!notificationId) return null;

            setIsLoading(true);
    
            const notificationObj = {
                "id": notificationId,
                "date": date ? date.toISOString() : null,             
                "title": title.trim(),
                "description": description.trim(),
                "businessUnit": parseInt(businessUnit),
                "businessPartner": null,
                "activity": null,
                "project": null
            }

            if (isRelatedToCustomer && selectedBusinessPartner && selectedBusinessPartner.Id) {
                notificationObj.businessPartner = parseInt(selectedBusinessPartner.Id);
            }

            if (isRelatedToActivity && selectedActivity && selectedActivity.Id) {
                notificationObj.activity = parseInt(selectedActivity.Id);
            }

            if (isRelatedToProject && selectedProject && selectedProject.Id) {
                notificationObj.project = parseInt(selectedProject.Id);
            }

            if (addToOutlook) {
                
                const outlookEvent = {
                    "subject": title.trim().toString(),
                    "body": {
                        "contentType": "HTML",
                        "content": description.trim()
                    },
                    "start": {
                        "dateTime": startDate.toISOString(),
                        "timeZone": "FLE Standard Time"
                    },
                    "end": {
                        "dateTime": endDate.toISOString(),
                        "timeZone": "FLE Standard Time"
                    },
                    "location": {
                        "displayName": location.trim().toString()
                    }
                }

                await createCalendarEvent(outlookEvent);
                // Do not block if event creation fails.
                //if (res && res.status && res.status !== 200) return;

            }

            const result = await updateNotification(notificationObj); 

            if (result && result.status && result.status === 200) {
                return history.goBack();
            }

            setIsLoading(false);

        } catch (error) {
            setIsLoading(false);
        }
    }

    return (
        <Form className="notification" onSubmit={(ev) => { ev.preventDefault(); onSubmit(); }} >
                
            <div ref={scollToTopRef}></div>
            
            <Loading active={ isLoading } text={t('general.loading')} />

            <PageTitle active title={t('notification.editNotification')} />

            <Message warning visible={projectHasEnded || projectIsNotActive} content={t('warnings.projectIsNotActive')} />

            {/* Title */}
            <Form.Field
                focus
                required
                label={t('general.title')}
                placeholder={t('general.title')}
                maxLength="255"
                control={Input}
                value={title}
                onChange={(ev, {value}) => setTitle(value) }
                type="Text"
            />

            {/* Notification is related to customer, activity or project? */}
            <Container className="fieldcontent">
                <Form.Field
                    control={Checkbox}
                    label={t('notification.isRelatedToCustomer')}
                    placeholder={t('notification.isRelatedToCustomer')}
                    checked={isRelatedToCustomer}
                    onChange={(ev, {checked}) => setIsRelatedToCustomer(checked) }
                />
                <Form.Field
                    control={Checkbox}
                    label={t('notification.isRelatedToActivity')}
                    placeholder={t('notification.isRelatedToActivity')}
                    checked={isRelatedToActivity}
                    onChange={(ev, {checked}) => setIsRelatedToActivity(checked) }
                />            
                <Form.Field
                    control={Checkbox}
                    label={t('notification.isRelatedToProject')}
                    placeholder={t('notification.isRelatedToProject')}
                    checked={isRelatedToProject}
                    onChange={(ev, {checked}) => setIsRelatedToProject(checked) }
                />
            </Container>

            {/* Search and select business partners if notification is related to business partner */}
            {isRelatedToCustomer && 
                <>
                    { /* Customer is NOT selected - Show searchbox */}
                    {!selectedBusinessPartner && 
                        <>
                            <Form.Field  label={t('businessPartner.searchCustomer')} style={{ marginTop: '20px'}} />
                            <SearchBusinessPartners
                                //title={t('businessPartner.customer')}
                                placeholder={t('businessPartner.searchCustomer')}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                selectedBusinessPartner={selectedBusinessPartner}
                                setSelectedBusinessPartner={setSelectedBusinessPartner}
                                setIsValidSelectedBusinessPartner={setIsValidSelectedCustomer}
                                type="customer"
                            />
                        </>
                    }
                    {/* Customer is selected - Show selected item */}
                    {selectedBusinessPartner &&
                        <SelectedItemLight
                            title={t('businessPartner.selectedCustomer')}
                            item={selectedBusinessPartner}
                            headers={[t('general.name'), t('businessPartner.area'), t('general.businessId')]}
                            fields={['Name', 'AreaName', 'AssociationNumber']}
                            required={true}
                            isValidSelectedItem={isValidSelectedCustomer}
                            onRemoveSelection={(ev) => {
                                setSelectedBusinessPartner(null);
                            }}
                        />
                    }
                </>
            }

            {/* Search and select activities if notification is related to activity */}         
            {isRelatedToActivity && 
                <>
                { /* Activity is NOT selected - Show searchbox */}
                    {!selectedActivity && 
                        <>
                            <Form.Field  label={t('activity.searchActivities')} style={{ marginTop: '20px'}} />
                            <SearchActivities
                                //title={t('activity.searchActivities')}
                                placeholder={t('activity.searchActivities')}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                selectedActivity={selectedActivity}
                                setSelectedActivity={setSelectedActivity}
                                setIsValidSelectedActivity={setIsValidSelectedActivity}
                            />
                        </>
                    }
                    {/* Activity is selected - Show selected item */}
                    {selectedActivity &&
                        <SelectedItemLight
                            title={t('activity.selectedActivity')}
                            item={selectedActivity}
                            headers={[t('general.title'), t('general.status'), t('general.businessPartner'), t('businessPartner.businessUnit')]}
                            fields={['Title', 'Objective', 'BusinessUnitName']}
                            required={true}
                            isValidSelectedItem={isValidSelectedActivity}
                            onRemoveSelection={(ev) => {
                                setSelectedActivity(null);
                            }}
                        />
                    }
                </>
            }

            {/* Search and select projects if notification is related to Project */}
            {isRelatedToProject && 
                <>
                    { /* Project is NOT selected - Show searchbox */}
                    {!selectedProject && 
                        <>
                            <Form.Field  label={t('project.searchProjects')} style={{ marginTop: '20px'}} />
                            <SearchProjects
                                placeholder={t('project.searchProjects')}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                selectedProject={selectedProject}
                                setSelectedProject={setSelectedProject}
                                setIsValidSelectedProject={setIsValidSelectedProject}
                            />
                        </>
                    }
                    {/* Project is selected - Show selected item */}
                    {selectedProject &&
                        <SelectedItemLight
                            title={t('project.selectedProject')}
                            item={selectedProject}
                            headers={[t('general.title'), t('general.status'), t('general.businessPartner'), t('businessPartner.businessUnit')]}
                            fields={['Title', 'StatusName', 'BusinessPartnerName', 'BusinessUnitName']}
                            required={true}
                            isValidSelectedItem={isValidSelectedProject}
                            onRemoveSelection={(ev) => {
                                setSelectedProject(null);
                            }}
                        />
                    }
                </>
            }

            {/* Description */}
            <Form.Field
                required
                label={t('general.description')}
                placeholder={t('general.description')}
                control={TextArea}
                value={description}
                onChange={(ev, {value}) => setDescription(value) }
                type="Text"
            />

            {/* Date */}
            <Container className="fieldcontent">
                <Form.Field label={t('notification.sendNotificationDate')} style={{ marginBottom: '0px' }}/>
                <DatePicker
                    required
                    label={t('notification.sendNotificationDate')}
                    selected={date}
                    dateFormat={navigator.language === "fi-FI" ? "dd.MM.yyyy" : "yyyy/MM/dd"}
                    onChange={(date) => setDate(date)}
                />
            </Container>

            <NewCalendarEvent
                addToOutlook={addToOutlook}
                setAddToOutlook={setAddToOutlook}
                title={title}
                body={description}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                location={location}
                setLocation={setLocation}
            />

            {/* Errors */}
            <Message error visible={!notificationId} content={t('errors.missingNotificationId')} />        

            {/* Buttons */}
            <Container className='button-container'>
                <CancelButton onClick={(ev) => { ev.preventDefault(); history.goBack(); }} />
                <SubmitButton type="submit" disabled={disableEditing && !userIsAdmin} className="bb-lblue" icon="save" title={t('general.save')} />
            </Container>

        </Form>
    );

}

export default EditNotification;
